import React, { useRef, useState, useEffect, useMemo } from 'react';
import PropTypes from "prop-types";
import { Table, Pagination, Button, Input, Space, Tag } from "antd";
import { Link } from "react-router-dom";
import { format } from 'date-fns';
import Highlighter from 'react-highlight-words';
import { SearchOutlined, TruckOutlined } from '@ant-design/icons';
import DatePickerMenu from '../dataPicker/dataPickerMenu';
import { CalendarOutlined } from '@ant-design/icons';
import { getFirestore, collection, query, where, orderBy, limit, getDocs, doc, onSnapshot } from "firebase/firestore";
// import "./client.css";
require("firebase/auth");
const db = getFirestore();

function normalizeStatusDescribe(record) {
  return String(record?.status?.describe || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function isInTransitRecord(record) {
  const s = normalizeStatusDescribe(record);
  return s.includes("em transito");
}

function isFinalizedLikeRecord(record) {
  const s = normalizeStatusDescribe(record);
  return s.includes("finalizado") || s.includes("entregue");
}

function parsePositionTimestampMs(lastPosition) {
  if (!lastPosition || lastPosition.timestamp == null) return null;
  const ts = lastPosition.timestamp;
  if (typeof ts === "number") return Number.isFinite(ts) ? ts : null;
  if (typeof ts === "string") {
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }
  if (typeof ts?.toMillis === "function") {
    return ts.toMillis();
  }
  if (typeof ts === "object" && typeof ts.seconds === "number") {
    return ts.seconds * 1000 + Math.floor((ts.nanoseconds || 0) / 1e6);
  }
  return null;
}

function parseDeadlineEndMs(record) {
  const raw =
    record?.firstDelivery?.dateDelivery ||
    record?.lastDelivery?.dateDelivery ||
    record?.freight?.firstDelivery?.dateDelivery;
  if (!raw) return null;
  if (typeof raw?.toDate === "function") {
    const d = raw.toDate();
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(23, 59, 59, 999);
    return d.getTime();
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    const m = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      return new Date(
        Number(m[1]),
        Number(m[2]) - 1,
        Number(m[3]),
        23,
        59,
        59,
        999
      ).getTime();
    }
    const d = new Date(trimmed);
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(23, 59, 59, 999);
    return d.getTime();
  }
  return null;
}

export function computeOperationalMeta(record) {
  const inTransit = isInTransitRecord(record);
  const finalizedLike = isFinalizedLikeRecord(record);

  const gpsMs = parsePositionTimestampMs(record.lastPosition);
  let gpsMinutesAgo = null;
  let gpsStale = false;
  if (inTransit) {
    if (gpsMs) {
      gpsMinutesAgo = Math.max(0, Math.floor((Date.now() - gpsMs) / 60000));
      gpsStale = gpsMinutesAgo > 45;
    } else {
      gpsStale = true;
    }
  }

  const offlineAlert = inTransit && record.online === "offline";
  const techAlert = inTransit && (gpsStale || offlineAlert);

  const deadlineEndMs = parseDeadlineEndMs(record);
  const prazoAlert = Boolean(inTransit && deadlineEndMs && Date.now() > deadlineEndMs);

  let sortKey = 0;
  if (!inTransit || finalizedLike) {
    sortKey = 0;
  } else if (prazoAlert && techAlert) {
    sortKey = 5;
  } else if (prazoAlert) {
    sortKey = 4;
  } else if (techAlert) {
    sortKey = 3;
  } else {
    sortKey = 1;
  }

  const labels = [];
  if (!inTransit || finalizedLike) {
    labels.push("Concluído");
  } else {
    if (prazoAlert) labels.push("Prazo");
    if (gpsStale) labels.push("GPS");
    if (offlineAlert) labels.push("Offline");
    if (!labels.length) labels.push("OK");
  }

  return {
    techAlert,
    prazoAlert,
    sortKey,
    gpsMinutesAgo,
    labels,
  };
}

export default function CustomPaginationTableFreights({
  list,
  operationalSegment = "all",
  onOperationalStats,
}) {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');

  const searchInput = useRef(null);

  const [dateRange, setDateRange] = useState([]);

  const [filteredData, setFilteredData] = useState(list); // Lista filtrada

  const [loading, setLoading] = useState(false);
  const [monitoringSourcesByDriver, setMonitoringSourcesByDriver] = useState({});
  const onOperationalStatsRef = useRef(onOperationalStats);
  onOperationalStatsRef.current = onOperationalStats;

  const paginatedData = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredData.slice(start, end);
  }, [filteredData, page, rowsPerPage]);

  // Função para aplicar filtragem
  const applyFilters = (updateData) => {
    let data = [...updateData];

    // Filtrar por texto
    if (searchText && searchedColumn) {
      data = data.filter((item) => {
        const value = item[searchedColumn];

        if (searchedColumn === 'clientPayment') {
          const clientPayment = item.clientPayment; // Certifique-se de que 'clientPayment' está sendo obtido do item
          if (clientPayment && typeof clientPayment.name === 'string') {
            const cleanValue = searchText.replace(/[^\w]/g, '').trim().toLowerCase();
            const cleanclientPayment = clientPayment.name.replace(/[^\w]/g, '').toLowerCase();

            return cleanclientPayment.includes(cleanValue);
          }
        }

        if (searchedColumn === 'firstDelivery') {
          const cleanValue = searchText.replace(/[^\w]/g, '').trim().toLowerCase(); // Limpa o valor de busca

          // Verifica se firstDelivery e os campos city e uf são válidos
          if (item.firstDelivery) {
            const cleanCity = item.firstDelivery.city ? item.firstDelivery.city.replace(/[^\w]/g, '').toLowerCase() : '';
            const cleanUF = item.firstDelivery.uf ? item.firstDelivery.uf.replace(/[^\w]/g, '').toLowerCase() : '';

            // Verifica se o valor limpo da cidade ou do estado inclui o valor de busca
            return cleanCity.includes(cleanValue) || cleanUF.includes(cleanValue);
          }
        }

        if (searchedColumn === 'lastDelivery') {
          const cleanValue = searchText.replace(/[^\w]/g, '').trim().toLowerCase();

          if (item.lastDelivery) {
            const cleanCity = item.lastDelivery.city ? item.lastDelivery.city.replace(/[^\w]/g, '').toLowerCase() : '';
            const cleanUF = item.lastDelivery.uf ? item.lastDelivery.uf.replace(/[^\w]/g, '').toLowerCase() : '';

            return cleanCity.includes(cleanValue) || cleanUF.includes(cleanValue);
          }
        }

        if (searchedColumn === 'status') {
          if (item.status && typeof item.status.describe === 'string') {
            const cleanValue = searchText.replace(/[^\w]/g, '').trim().toLowerCase();
            const cleanStatus = item.status.describe.replace(/[^\w]/g, '').toLowerCase();

            return cleanStatus.includes(cleanValue);
          }
        }

        if (searchedColumn === 'stoppingQt') {
          if (item.stoppingQt && typeof item.stoppingQt === 'number') {
            const cleanValue = parseInt(searchText, 10);

            return item.stoppingQt === cleanValue;
          }
        }

        return value && value.toString().toLowerCase().includes(searchText.toLowerCase());
      });
    }

    // Filtrar por intervalo de datas
    if (dateRange && dateRange.length) {
      const [startDate, endDate] = dateRange;
      data = data.filter((record) => {
        const dateColeta = new Date(record?.firstDelivery?.dateColeta);
        if (isNaN(dateColeta.getTime())) return false;

        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;
        if (start && end && start.getTime() === end.getTime()) {
          return dateColeta.toDateString() === start.toDateString();
        }
        if (start && !end) {
          return dateColeta >= start;
        }
        if (start && end) {
          end.setHours(23, 59, 59, 999);
          return dateColeta >= start && dateColeta <= end;
        }
        return true;
      });
    }

    setFilteredData(data); // Atualiza a lista filtrada
    setPage(1); // Reinicia a paginação para a primeira página
  };

  function formatTimestamp(timestamp) {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    const dia = String(date.getDate()).padStart(2, "0");
    const mes = String(date.getMonth() + 1).padStart(2, "0");
    const ano = date.getFullYear();
    const horas = String(date.getHours()).padStart(2, "0");
    const minutos = String(date.getMinutes()).padStart(2, "0");

    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }

  function parseMonitoringTimestamp(timestamp) {
    if (!timestamp) return null;

    if (typeof timestamp?.toMillis === "function") {
      return timestamp.toMillis();
    }

    if (typeof timestamp === "number") {
      return timestamp;
    }

    if (typeof timestamp === "string") {
      const parsedDate = new Date(timestamp).getTime();
      return Number.isNaN(parsedDate) ? null : parsedDate;
    }

    if (
      typeof timestamp === "object" &&
      typeof timestamp?.seconds === "number"
    ) {
      return (timestamp.seconds * 1000) + Math.floor((timestamp.nanoseconds || 0) / 1000000);
    }

    return null;
  }

  function isDriverOnlineNow(status) {
    if (!status) return false;

    const executionState = status?.execution_state || status?.app_state || "unknown";

    return (
      status?.is_online === true &&
      executionState !== "terminated"
    );
  }

  function getLastSeenAt(status) {
    const lastSeenMs = parseMonitoringTimestamp(status?.last_seen_at);
    return lastSeenMs ? new Date(lastSeenMs) : null;
  }

  function buildMonitoringStatus(monitoringStatus = {}) {
    const lastSeenAt = getLastSeenAt(monitoringStatus);
    const isOnlineNow = isDriverOnlineNow(monitoringStatus);

    return {
      ...monitoringStatus,
      isOnlineNow,
      lastSeenAtFormatted: lastSeenAt ? formatTimestamp(lastSeenAt.getTime()) : null,
      lastOnlineAtFormatted: formatTimestamp(parseMonitoringTimestamp(monitoringStatus?.last_online_at)),
    };
  }

  function getMonitoringStatusForDriver(uidDriver) {
    if (!uidDriver) return buildMonitoringStatus({});

    const source = monitoringSourcesByDriver[uidDriver] || {};
    const monitoringData = source.primary || source.compatibility || {};

    return buildMonitoringStatus(monitoringData);
  }

  async function addLastValidPositionAll(data) {
    const promises = data.map(async (item) => {
      let lastPosition = null;
      const uidDriver = item?.freight?.getDriverFreight?.uidDriver;
      const monitoringStatus = getMonitoringStatusForDriver(uidDriver);
      const isOnlineNow = monitoringStatus?.isOnlineNow === true;

      const positionsRef = collection(db, "freight", item.id, "positions");
      const q = query(positionsRef, orderBy("timestamp", "desc"), limit(1));
      const snapshot = await getDocs(q);

      snapshot.forEach((doc) => {
        const pos = doc.data();
        lastPosition = { 
          id: doc.id, 
          ...pos, 
          timestamp: pos.timestamp,
          formattedTimestamp: formatTimestamp(pos.timestamp) 
        };
      });

      return {
        ...item,
        online: isOnlineNow ? "online" : "offline",
        monitoringStatus,
        lastPosition: lastPosition
          ? lastPosition
          : { formattedTimestamp: null }, // sempre existe
      };
    });

    return Promise.all(promises);
  }

  useEffect(() => {
    let intervalId;

    async function fetchData() {
      setLoading(true);
      try {
        const withPositions = await addLastValidPositionAll(list);
        const withMeta = withPositions.map((row) => ({
          ...row,
          _operational: computeOperationalMeta(row),
        }));

        let comAlerta = 0;
        let atrasoPrazo = 0;
        withMeta.forEach((row) => {
          if (row._operational.techAlert) comAlerta += 1;
          if (row._operational.prazoAlert) atrasoPrazo += 1;
        });
        const emitStats = onOperationalStatsRef.current;
        if (typeof emitStats === "function") {
          emitStats({ comAlerta, atrasoPrazo, totalNaLista: withMeta.length });
        }

        let segmentRows = withMeta;
        if (operationalSegment === "com_alerta") {
          segmentRows = withMeta.filter((row) => row._operational.techAlert);
        } else if (operationalSegment === "atraso_prazo") {
          segmentRows = withMeta.filter((row) => row._operational.prazoAlert);
        }

        segmentRows.sort((a, b) => (b._operational?.sortKey || 0) - (a._operational?.sortKey || 0));

        applyFilters(segmentRows);
      } catch (error) {
        console.error("Erro ao atualizar lista:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
    intervalId = setInterval(fetchData, 3 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [list, searchText, searchedColumn, dateRange, operationalSegment]);

  useEffect(() => {
    const driverUids = Array.from(
      new Set(
        list
          .map((item) => item?.freight?.getDriverFreight?.uidDriver)
          .filter(Boolean)
      )
    );

    if (!driverUids.length) return () => {};

    const unsubscribers = [];

    driverUids.forEach((uidDriver) => {
      const primaryRef = doc(db, "drivers_users", uidDriver, "monitoring", "status");

      const unsubscribePrimary = onSnapshot(primaryRef, (snapshot) => {
        setMonitoringSourcesByDriver((prev) => {
          const current = prev[uidDriver] || {};
          return {
            ...prev,
            [uidDriver]: {
              ...current,
              primary: snapshot.exists() ? (snapshot.data() || {}) : null,
            },
          };
        });
      });
      unsubscribers.push(unsubscribePrimary);
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [list]);

  useEffect(() => {
    setFilteredData((prevData) =>
      prevData.map((item) => {
        const uidDriver = item?.freight?.getDriverFreight?.uidDriver;
        const monitoringStatus = getMonitoringStatusForDriver(uidDriver);
        const isOnlineNow = monitoringStatus?.isOnlineNow === true;

        const updated = {
          ...item,
          online: isOnlineNow ? "online" : "offline",
          monitoringStatus,
        };
        return {
          ...updated,
          _operational: computeOperationalMeta(updated),
        };
      })
    );
  }, [monitoringSourcesByDriver]);

  const handleDateRangeChange = (dates) => {
    setDateRange(dates ? [dates[0], dates[1]] : []);
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleSearchDate = (confirm) => {
    confirm();
    setSearchText('');
    setSearchedColumn('dateColeta');
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const handleResetDate = (clearFilters) => {
    clearFilters();
    setDateRange([]);
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({
                closeDropdown: false,
              });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1677ff' : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>{ 

      console.log(dataIndex);
      console.log(value);
      console.log(record);
      if (dataIndex === "clientPayment") {
        if (record.clientPayment && typeof record.clientPayment.name === "string") {
          const cleanValue = value.trim().toLowerCase();
          const cleanClientPayment = record.clientPayment.name.toLowerCase();
          return cleanClientPayment.includes(cleanValue);
        }
        return false;
      }
      if (dataIndex === 'firstDelivery') {
        const cleanValue = value.replace(/[^\w]/g, '').trim().toLowerCase(); // Limpa o valor de busca

        // Verifica se firstDelivery e os campos city e uf são válidos
        if (record.firstDelivery) {
          const cleanCity = record.firstDelivery.city ? record.firstDelivery.city.replace(/[^\w]/g, '').toLowerCase() : '';
          const cleanUF = record.firstDelivery.uf ? record.firstDelivery.uf.replace(/[^\w]/g, '').toLowerCase() : '';

          // Verifica se o valor limpo da cidade ou do estado inclui o valor de busca
          return cleanCity.includes(cleanValue) || cleanUF.includes(cleanValue);
        }
      }
      if (dataIndex === 'lastDelivery') {
        const cleanValue = value.replace(/[^\w]/g, '').trim().toLowerCase();

        if (record.lastDelivery) {
          const cleanCity = record.lastDelivery.city ? record.lastDelivery.city.replace(/[^\w]/g, '').toLowerCase() : '';
          const cleanUF = record.lastDelivery.uf ? record.lastDelivery.uf.replace(/[^\w]/g, '').toLowerCase() : '';

          return cleanCity.includes(cleanValue) || cleanUF.includes(cleanValue);
        }
      }
      if (dataIndex === 'status') {
        if (record.status && typeof record.status.describe === 'string') {
          const cleanValue = value.replace(/[^\w]/g, '').trim().toLowerCase();
          const cleanStatus = record.status.describe.replace(/[^\w]/g, '').toLowerCase();

          return cleanStatus.includes(cleanValue);
        }
      }
      if (dataIndex === 'stoppingQt') {
        if (record.stoppingQt && typeof record.stoppingQt === 'number') {
          const cleanValue = parseInt(value, 10);

          return record.stoppingQt === cleanValue;
        }
      }
      else {
        return record[dataIndex]
          ? String(record[dataIndex]).toLowerCase().includes(value.toLowerCase())
          : false;
      }

    }, onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },

    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: '#ffc069',
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const getColumnSearchPropsDate = () => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <DatePickerMenu
          onChange={(dates) => {
            handleDateRangeChange(dates); // Atualiza o estado do intervalo de datas
            setSelectedKeys(dates ? [dates] : []); // Armazena o intervalo de datas selecionado
          }}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearchDate(confirm)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleResetDate(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <CalendarOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (_, record) => {
      if (!dateRange || !dateRange.length) return true; // Se não há intervalo de datas

      const [startDate, endDate] = dateRange;
      const dateColeta = new Date(record?.firstDelivery?.dateColeta);
      
    
      if (isNaN(dateColeta.getTime())) {
        // Se a data for inválida
        return false;
      }

      // limpeza startDate e endDate
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      if (start && end && start.getTime() === end.getTime()) {
        // Se startDate e endDate são iguais 
        return dateColeta.toDateString() === start.toDateString();
      }

      if (end) {
        // Ajusta o horário do endDate para o final do dia
        end.setHours(23, 59, 59, 999);
      }
  
      if (start && !end) {
        // Se endDate não estiver definido data em ou após startDate
        return dateColeta >= start;
      }

      if (start && end) {
        // Se startDate e endDate estão definidos, intervalo entre startDate e endDate
        return dateColeta >= start && dateColeta <= end;
      }

      return true; // Caso não se encaixe em nenhuma condição
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === 'dateColeta' ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (current, pageSize) => {
    setRowsPerPage(pageSize);
    setPage(1);
  };

  const tableEmptyText = useMemo(() => {
    let title = "No momento não há fretes nesta visão";
    let subtitle = "Assim que houver fretes para acompanhar, eles aparecerão aqui.";
    if (operationalSegment === "com_alerta") {
      title = "Nenhum frete com alerta operacional";
      subtitle = "Alertas consideram GPS sem atualização há mais de 45 minutos ou motorista offline em viagem em trânsito.";
    } else if (operationalSegment === "atraso_prazo") {
      title = "Nenhum frete com prazo de entrega ultrapassado";
      subtitle = "Comparamos a data de entrega acordada com a data atual para fretes ainda em trânsito.";
    }
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 20px",
          color: "#64748b",
        }}
      >
        <TruckOutlined style={{ fontSize: 56, marginBottom: 14, color: "#cbd5e1" }} />
        <span style={{ fontSize: 16, fontWeight: 600, color: "#012443" }}>{title}</span>
        <span style={{ fontSize: 14, fontWeight: 400, marginTop: 6, textAlign: "center", maxWidth: 420 }}>
          {subtitle}
        </span>
      </div>
    );
  }, [operationalSegment]);

  const tagColorForLabel = (label) => {
    if (label === "OK") return "success";
    if (label === "Concluído") return "processing";
    if (label === "Prazo") return "error";
    if (label === "GPS") return "warning";
    if (label === "Offline") return "volcano";
    return "default";
  };

  const columns = [
    {
      title: "Operação",
      key: "operacao",
      width: 220,
      render: (_, record) => {
        const op = record._operational || computeOperationalMeta(record);
        const showGps =
          isInTransitRecord(record) &&
          op.gpsMinutesAgo != null &&
          !isFinalizedLikeRecord(record);
        return (
          <Space direction="vertical" size={4} style={{ maxWidth: 210 }}>
            <Space size={4} wrap>
              {(op.labels || []).map((label) => (
                <Tag key={label} color={tagColorForLabel(label)}>
                  {label}
                </Tag>
              ))}
            </Space>
            {showGps && (
              <span style={{ fontSize: 12, color: "#64748b" }}>
                Último GPS há {op.gpsMinutesAgo} min
              </span>
            )}
          </Space>
        );
      },
      sorter: (a, b) => (a._operational?.sortKey || 0) - (b._operational?.sortKey || 0),
      defaultSortOrder: "descend",
    },
    {
      title: "Motorista",
      dataIndex: "driverName",
      key: "driverName",
      ...getColumnSearchProps('driverName'),
      render: (text, record) => (
        <Link
          to={`/freightsDetail/${record.id}/${record.freight.getDriverFreight.uidDriver}`}
          style={{ cursor: "pointer", textDecoration: "none", color: "black" }}
        >
          {text}
        </Link>
      ),
      sorter: (a, b) => {
        const nameA = a.driverName || '';
        const nameB = b.driverName || '';
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: "Frete",
      dadaIndex: "numberSerial",
      key: "numberSerial",
      ...getColumnSearchProps('numberSerial'),
      render: (text, record) => (
        <Link 
          to={`/freightCopy/${record.id}`}
          style={{ cursor: "pointer", textDecoration: "none", color: "black" }}
        >
          {record.numberSerial}
        </Link>
      ),
      sorter: ( a, b ) => {
        const aA = a.record?.numberSerial;
        const bB = b.record?.numberSerial;

        if (!aA) return -1;
        if (!bB) return 1;
        
        return aA - bB;
      },
    },
    {
      title: "Data Carregamento",
      dataIndex: "firstDelivery",
      key: "firstDelivery",
      ...getColumnSearchPropsDate(),
      sorter: (a, b) => {
        const dateA = a.firstDelivery?.dateColeta;
        const dateB = b.firstDelivery?.dateColeta;

        if (!dateA) return -1;
        if (!dateB) return 1;

        const [yearA, monthA, dayA] = dateA.split('-').map(Number);
        const [yearB, monthB, dayB] = dateB.split('-').map(Number);

        const timeA = new Date(yearA, monthA - 1, dayA).getTime();
        const timeB = new Date(yearB, monthB - 1, dayB).getTime();

        return timeA - timeB;
      },
      render: (firstDelivery) => {
        if (!firstDelivery || !firstDelivery?.dateColeta) {
          return 'Data inválida';
        }
    
        const parsedDate = new Date(firstDelivery.dateColeta);
    
        if (isNaN(parsedDate.getTime())) {
          // Se a data for inválida
          console.error('Data inválida detectada:', firstDelivery.dateColeta);
          return 'Data inválida';
        }
    
        try {
          return format(parsedDate, 'dd/MM/yyyy');
        } catch (error) {
          console.error('Erro ao formatar a data:', error);
          return 'Data inválida';
        }
      },
    },    
    {
      title: "Cliente",
      dataIndex: "clientPayment",
      key: "clientPayment",
      ...getColumnSearchProps('clientPayment'),
      render: (clientPayment) => clientPayment.name,
      sorter: (a, b) => {
        const nameA = a.clientPayment.name || '';
        const nameB = b.clientPayment.name || '';
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: "Origem",
      dataIndex: "firstDelivery",
      key: "origin",
      ...getColumnSearchProps('firstDelivery'),
      render: (firstDelivery) => `${firstDelivery.city} - ${firstDelivery.uf}`,
      sorter: (a, b) => {
        const nameA = `${a.firstDelivery.city || ''} ${a.firstDelivery.uf || ''}`.trim();
        const nameB = `${b.firstDelivery.city || ''} ${b.firstDelivery.uf || ''}`.trim();
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: "Destino",
      dataIndex: "lastDelivery",
      key: "destination",
      ...getColumnSearchProps('lastDelivery'),
      render: (lastDelivery) => `${lastDelivery.city} - ${lastDelivery.uf}`,
      sorter: (a, b) => {
        const nameA = `${a.lastDelivery.city || ''} ${a.lastDelivery.uf || ''}`.trim();
        const nameB = `${b.lastDelivery.city || ''} ${b.lastDelivery.uf || ''}`.trim();
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: "Qtd de Entregas",
      dataIndex: "stoppingQt",
      key: "stoppingQt",
      ...getColumnSearchProps('stoppingQt'),
      sorter: (a, b) => a.stoppingQt - b.stoppingQt,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: "Online",
      dataIndex: "online",
      key: "online",
      ...getColumnSearchProps('online'),
      sorter: (a, b) => {
        const statusOrder = { online: 1, offline: 2 };
        return (statusOrder[a.online] || 99) - (statusOrder[b.online] || 99);
      },
      render: (online, record) => (
        <span style={{ color: online === 'online' ? 'green' : 'red', fontWeight: 'bold' }}>
          {online === 'online' ? 'Está online agora' : 'Offline'}
          <br />
          <span style={{ color: "#666", fontWeight: 400 }}>
            Última vez que esteve online: {record.monitoringStatus?.lastOnlineAtFormatted || "Sem registro"}
          </span>
          <br />
          <span style={{ color: "#666", fontWeight: 400 }}>
            Última atividade: {record.monitoringStatus?.lastSeenAtFormatted || "Sem registro"}
          </span>
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      ...getColumnSearchProps('status'),
      render: (status) => status.describe,
      sorter: (a, b) => {
        const nameA = a.status.describe || '';
        const nameB = b.status.describe || '';
        return nameA.localeCompare(nameB);
      },
    },
  ];

  return (
    <>
      <Table
        dataSource={paginatedData}
        columns={columns}
        pagination={false}
        rowKey="id"
        scroll={{ x: 1280 }}
        style={{ marginBottom: '16px' }}
        loading={loading}
        locale={{
          emptyText: tableEmptyText,
        }}
      />
      {filteredData.length === list.length ? (
        <Pagination
          current={page}
          pageSize={rowsPerPage}
          total={list.length}
          showSizeChanger
          pageSizeOptions={[10, 25, 50]}
          onChange={handleChangePage}
          onShowSizeChange={handleChangeRowsPerPage}
          showTotal={(total) => `Total de ${total} fretes`}
        />
      ) : 
        <Pagination
          current={page}
          pageSize={rowsPerPage}
          total={filteredData.length}
          showSizeChanger
          pageSizeOptions={[10, 25, 50]}
          onChange={handleChangePage}
          onShowSizeChange={handleChangeRowsPerPage}
          showTotal={(total) => `Total de ${total} fretes`}
        />
      }
    </>
  );
}

CustomPaginationTableFreights.propTypes = {
  list: PropTypes.array.isRequired,
  operationalSegment: PropTypes.oneOf(["all", "com_alerta", "atraso_prazo"]),
  onOperationalStats: PropTypes.func,
};